import React, { useState, useMemo, useRef } from 'react';
import { X, Landmark, Info } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { MoneyInput } from './ui/MoneyInput';
import { DatePicker } from './ui/DatePicker';
import { Tooltip } from './ui/Tooltip';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { deudaService } from '../services/deudaService';
import { DeudaConEstado } from '../lib/supabase';
import { OwnerContext } from '../types/actividad';
import { calcularValorCuota } from '../utils/deudaCalculations';
import { fechaInicioMes, hoyEnChile } from '../utils/dateHelpers';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { OrigenDeudaCampos } from './OrigenDeudaCampos';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { MENSAJE_ORIGEN_FALTANTE, montoRecibidoDelPrestamo, validarOrigenDeuda, type Deposito, type OrigenDeuda } from '../utils/origenDeuda';

const noop = () => {};

/** Fecha del primer vencimiento a precargar en el formulario: la de la deuda (edición,
 * reconstruida como fecha real a partir de mes_primer_vencimiento + dia_vencimiento —
 * ambos provienen siempre de la misma fecha original, así que la reconstrucción es válida)
 * o hoy (crear). */
function fechaPrimerVencimientoInicial(deuda?: Pick<DeudaConEstado, 'mes_primer_vencimiento' | 'dia_vencimiento'>): string {
  if (!deuda) return hoyEnChile();
  const [anio, mes] = deuda.mes_primer_vencimiento.split('-');
  return `${anio}-${mes}-${String(deuda.dia_vencimiento).padStart(2, '0')}`;
}

/** Valores con que arranca el formulario: los de la deuda al editar, vacíos al crear. */
function valoresIniciales(deuda?: DeudaConEstado) {
  return {
    acreedor: deuda?.acreedor ?? '',
    descripcion: deuda?.descripcion ?? '',
    montoTotal: deuda?.monto_total ?? 0,
    numeroCuotas: deuda?.numero_cuotas ?? 0,
    fechaInicioCredito: deuda?.fecha_inicio_credito ?? hoyEnChile(),
    fechaPrimerVencimiento: fechaPrimerVencimientoInicial(deuda),
  };
}

/** Una compra a crédito pide qué se compró; cualquier otro origen, el motivo general. */
function placeholderDelMotivo(origenDeuda: OrigenDeuda | ''): string {
  return origenDeuda === 'compra_credito'
    ? 'Qué se compró (ej: refrigerador para la cocina)'
    : 'Ej: Compra de terreno, gastos generales...';
}

function textoDelBoton(esEdicion: boolean, submitting: boolean): string {
  if (submitting) return 'Guardando...';
  return esEdicion ? 'Guardar cambios' : 'Registrar deuda';
}

/** Preview del valor de cada cuota mientras el usuario completa el formulario; 0 si aún no hay datos suficientes. */
function calcularPreviewCuota(montoTotal: number, numeroCuotas: number): number {
  if (montoTotal <= 0 || numeroCuotas <= 0) return 0;
  return calcularValorCuota(montoTotal, numeroCuotas);
}

const AYUDA_CUOTAS_PAGADAS = 'Si el crédito comenzó en el pasado y ya se pagaron cuotas antes de registrar esta deuda en el sistema, indica aquí cuántas.';

// Estático — hoisteado para que no se recree en cada render (jsx-no-jsx-as-prop).
const LABEL_HISTORIA_PREVIA = (
  <Tooltip content={AYUDA_CUOTAS_PAGADAS} position="top">
    <Info className="w-4 h-4 text-slate-400" />
  </Tooltip>
);

interface ValidarFormularioDeudaInput {
  acreedor: string;
  descripcion: string;
  montoTotal: number;
  numeroCuotas: number;
  fechaInicioCredito: string;
  fechaPrimerVencimiento: string;
  cuotasPreviasPagadas: string;
  esEdicion: boolean;
  montoPagado: number;
}

interface HayCambiosSinGuardarInput {
  submitting: boolean;
  esEdicion: boolean;
  acreedor: string;
  descripcion: string;
  montoTotal: number;
  numeroCuotas: number;
  fechaInicioCredito: string;
  fechaPrimerVencimiento: string;
  cuotasPreviasPagadas: string;
  /** Alta: ya se eligió un comprobante o se respondió qué originó la deuda. */
  origenIniciado: boolean;
  deuda?: DeudaConEstado;
}

/** Extraída del componente (fuera de su cognitive-complexity) — ver .claude/rules/ui-componentes.md. */
function hayCambiosSinGuardar(input: HayCambiosSinGuardarInput): boolean {
  const {
    submitting, esEdicion, acreedor, descripcion, montoTotal, numeroCuotas,
    fechaInicioCredito, fechaPrimerVencimiento, cuotasPreviasPagadas, origenIniciado, deuda,
  } = input;
  if (submitting) return true;
  if (!esEdicion) {
    return origenIniciado || acreedor !== '' || descripcion !== '' || montoTotal > 0 || numeroCuotas > 0 || cuotasPreviasPagadas !== '';
  }
  return (
    acreedor !== deuda!.acreedor ||
    descripcion !== (deuda!.descripcion ?? '') ||
    montoTotal !== deuda!.monto_total ||
    numeroCuotas !== deuda!.numero_cuotas ||
    fechaInicioCredito !== deuda!.fecha_inicio_credito ||
    fechaPrimerVencimiento !== fechaPrimerVencimientoInicial(deuda)
  );
}

/** Extraída del componente (fuera de su cognitive-complexity) — ver .claude/rules/ui-componentes.md. */
function validarFormularioDeuda(input: ValidarFormularioDeudaInput): string | null {
  const {
    acreedor, descripcion, montoTotal, numeroCuotas, fechaInicioCredito, fechaPrimerVencimiento,
    cuotasPreviasPagadas, esEdicion, montoPagado,
  } = input;
  if (!acreedor.trim()) return 'Indica a quién se le paga esta deuda';
  if (!descripcion.trim()) return 'Indica el motivo de la deuda';
  if (montoTotal <= 0) return 'El monto debe ser mayor a cero';
  if (numeroCuotas < 1) return 'La deuda debe tener al menos una cuota';
  if (calcularValorCuota(montoTotal, numeroCuotas) <= 0) return 'El monto total es muy bajo para el número de cuotas indicado';
  if (!fechaInicioCredito) return 'Indica la fecha de inicio del crédito';
  if (!fechaPrimerVencimiento) return 'Indica la fecha del primer vencimiento';
  if (fechaInicioCredito.slice(0, 7) > fechaPrimerVencimiento.slice(0, 7)) {
    return 'La fecha de inicio del crédito no puede ser posterior al mes de la primera cuota';
  }
  if (esEdicion && montoTotal < montoPagado) return 'El monto total no puede ser menor a lo ya pagado';
  if (!esEdicion && cuotasPreviasPagadas) {
    const previas = Number.parseInt(cuotasPreviasPagadas, 10);
    if (!Number.isNaN(previas) && previas > numeroCuotas) return 'Todas las cuotas de esta deuda ya están pagadas';
  }
  return null;
}

interface DeudaModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  /** Requerido solo al crear (deuda ausente); ignorado al editar. */
  readonly owner?: OwnerContext;
  readonly deuda?: DeudaConEstado;
  /** 'nested' cuando se abre sobre el detalle de la deuda (edición); 'base' al crear desde la lista. */
  readonly level?: 'base' | 'nested';
}

export const DeudaModal: React.FC<DeudaModalProps> = ({ isOpen, onClose, onSuccess, owner, deuda, level = 'base' }) => {
  const esEdicion = deuda !== undefined;

  const [acreedor, setAcreedor] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [montoTotal, setMontoTotal] = useState(0);
  const [numeroCuotas, setNumeroCuotas] = useState(0);
  const [fechaInicioCredito, setFechaInicioCredito] = useState(hoyEnChile);
  const [fechaPrimerVencimiento, setFechaPrimerVencimiento] = useState(hoyEnChile);
  const [cuotasPreviasPagadas, setCuotasPreviasPagadas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [origenDeuda, setOrigenDeuda] = useState<OrigenDeuda | ''>('');
  const [depositado, setDepositado] = useState<Deposito | ''>('');
  const [montoRecibido, setMontoRecibido] = useState(0);

  const { error, clearError, handleError } = useErrorHandler();
  // El comprobante de origen solo se pide al crear; en edición el bloque ni se muestra.
  const comprobante = useComprobanteFormulario({
    entidad: owner && owner.tipo !== 'global' ? { tipo: owner.tipo, id: owner.id, nombre: owner.nombre } : null,
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: false,
    mensajeFaltante: MENSAJE_ORIGEN_FALTANTE,
  });

  // Reinicia el formulario cuando el modal pasa de cerrado a abierto (o cambia la deuda
  // a editar) — comparación durante el render, no en useEffect: evita el frame con UI
  // stale que produce un efecto (ver .claude/rules/ui-componentes.md).
  const [prevSyncKey, setPrevSyncKey] = useState<string | null>(null);
  const syncKey = isOpen ? `${isOpen}-${deuda?.id ?? 'crear'}` : null;
  if (syncKey !== prevSyncKey) {
    setPrevSyncKey(syncKey);
    if (isOpen) {
      const inicial = valoresIniciales(deuda);
      setAcreedor(inicial.acreedor);
      setDescripcion(inicial.descripcion);
      setMontoTotal(inicial.montoTotal);
      setNumeroCuotas(inicial.numeroCuotas);
      setFechaInicioCredito(inicial.fechaInicioCredito);
      setFechaPrimerVencimiento(inicial.fechaPrimerVencimiento);
      setCuotasPreviasPagadas('');
      setOrigenDeuda('');
      setDepositado('');
      setMontoRecibido(0);
      clearError();
    }
  }

  const valorCuotaCalculado = calcularPreviewCuota(montoTotal, numeroCuotas);

  const hasUnsavedChanges = useMemo(
    () => hayCambiosSinGuardar({
      submitting, esEdicion, acreedor, descripcion, montoTotal, numeroCuotas,
      fechaInicioCredito, fechaPrimerVencimiento, cuotasPreviasPagadas, deuda,
      origenIniciado: origenDeuda !== '' || comprobante.hayCambios,
    }),
    [submitting, esEdicion, acreedor, descripcion, montoTotal, numeroCuotas, fechaInicioCredito, fechaPrimerVencimiento, cuotasPreviasPagadas, deuda, origenDeuda, comprobante.hayCambios],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const errorOrigen = esEdicion ? null
      : validarOrigenDeuda({ comprobanteExigible: comprobante.exigible, origenDeuda, depositado, montoRecibido, montoTotal })
        ?? comprobante.validar();
    if (errorOrigen) { handleError(errorOrigen); return; }

    const validationError = validarFormularioDeuda({
      acreedor, descripcion, montoTotal, numeroCuotas, fechaInicioCredito, fechaPrimerVencimiento, cuotasPreviasPagadas,
      esEdicion, montoPagado: deuda?.pagado ?? 0,
    });
    if (validationError) { handleError(validationError); return; }

    const [anio, mes, dia] = fechaPrimerVencimiento.split('-').map(Number);
    const mesPrimerVencimiento = fechaInicioMes(mes, anio);

    try {
      setSubmitting(true);
      if (esEdicion) {
        await deudaService.editar(deuda!.id, {
          acreedor: acreedor.trim(),
          descripcion: descripcion.trim(),
          monto_total: montoTotal,
          numero_cuotas: numeroCuotas,
          fecha_inicio_credito: fechaInicioCredito,
          mes_primer_vencimiento: mesPrimerVencimiento,
          dia_vencimiento: dia,
        });
      } else {
        if (!owner) throw new Error('Falta la entidad dueña para crear la deuda');
        const previas = cuotasPreviasPagadas ? Number.parseInt(cuotasPreviasPagadas, 10) : undefined;
        const respaldo = await comprobante.resolver();
        await deudaService.crear({
          owner,
          acreedor: acreedor.trim(),
          descripcion: descripcion.trim(),
          monto_total: montoTotal,
          numero_cuotas: numeroCuotas,
          fecha_inicio_credito: fechaInicioCredito,
          mes_primer_vencimiento: mesPrimerVencimiento,
          dia_vencimiento: dia,
          cuotasPreviasPagadas: previas,
          origen_deuda: origenDeuda || null,
          comprobante_origen_id: respaldo.comprobante_id,
          monto_recibido: montoRecibidoDelPrestamo(origenDeuda, depositado, montoRecibido),
        });
      }
      comprobante.reiniciar();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al guardar la deuda. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();
  // Al cerrar sin guardar se sueltan las vistas previas del comprobante elegido.
  const cerrar = () => { comprobante.reiniciar(); onClose(); };

  if (!isOpen) return null;

  const textoBotonSubmit = textoDelBoton(esEdicion, submitting);

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={cerrar}
      size="md"
      level={level}
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Landmark className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">{esEdicion ? 'Editar deuda' : 'Registrar deuda'}</h2>
          </div>
          <button type="button" onClick={requestClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormErrorAlert error={error} onClear={clearError} />

        {!esEdicion && (
          <OrigenDeudaCampos
            comprobante={comprobante}
            origenDeuda={origenDeuda}
            onOrigenChange={(origen) => { setOrigenDeuda(origen); setDepositado(''); }}
            depositado={depositado}
            onDepositadoChange={(valor) => { setDepositado(valor); if (valor === 'si') setMontoRecibido(montoTotal); }}
            montoRecibido={montoRecibido}
            onMontoRecibidoChange={setMontoRecibido}
            montoTotal={montoTotal}
            nombreEntidad={owner?.nombre ?? ''}
            deshabilitado={submitting}
          />
        )}

        <TextInput label="Acreedor" value={acreedor} onChange={setAcreedor} placeholder="Ej: Persona X, Banco X..." required />

        <TextInput label="Motivo" value={descripcion} onChange={setDescripcion} placeholder={placeholderDelMotivo(origenDeuda)} required />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MoneyInput label="Monto total" value={montoTotal} onChange={setMontoTotal} required />
          <MoneyInput label="Cuotas" value={numeroCuotas} onChange={setNumeroCuotas} min={1} showPrefix={false} required disabled={esEdicion} />
          <MoneyInput label="Valor de cuota" value={valorCuotaCalculado} onChange={noop} disabled />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker label="Inicio del crédito" value={fechaInicioCredito} onChange={setFechaInicioCredito} required />
          <DatePicker label="Primer vencimiento" value={fechaPrimerVencimiento} onChange={setFechaPrimerVencimiento} min={fechaInicioCredito} required />
        </div>
        {!esEdicion && origenDeuda === 'prestamo' && depositado === 'si' && (
          <p className="text-sm text-slate-500">La entrada por financiamiento se registra con el día de registro en Chile. El inicio del crédito conserva la fecha que indiques arriba.</p>
        )}

        {!esEdicion && (
          <TextInput
            label="Cuotas pagadas"
            labelHelp={LABEL_HISTORIA_PREVIA}
            type="number"
            min={0}
            value={cuotasPreviasPagadas}
            onChange={setCuotasPreviasPagadas}
            placeholder="0"
          />
        )}

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={requestClose}
            className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || comprobante.subiendo}
          >
            {textoBotonSubmit}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
